import type { ReactNode } from "react";
import styled from "styled-components";
import { fromJS } from "immutable";
import Select, {
  ClearIndicatorProps,
  CSSObjectWithLabel,
  GroupBase,
  MenuProps,
  MultiValue,
  Props,
  Theme,
  components
} from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";

import { theme } from "config/theme";
import FieldsHOC, { FieldHocProps } from "../fields-hoc";

export interface LegacySelectOption {
  label: ReactNode;
  value: unknown;
}

type SelectGroup = GroupBase<LegacySelectOption>;
type LegacySelectProps = Props<LegacySelectOption, true, SelectGroup>;

interface StyledSelectProps {
  margin?: string;
  flex?: string;
  width?: string;
  minWidth?: string | number;
  limit?: number;
  error?: unknown;
  inputDisabled?: boolean;
}

export type UpReactSelectProps = Omit<
  LegacySelectProps,
  "components" | "isMulti" | "styles" | "theme"
> &
  StyledSelectProps;

export const RemoveIcon = styled(FontAwesomeIcon)`
  width: 15px !important;
  height: 15px !important;
`;

export const ClearIndicator = (
  props: ClearIndicatorProps<LegacySelectOption, true, SelectGroup>
) => (
  <components.ClearIndicator {...props}>
    <RemoveIcon icon="times" />
  </components.ClearIndicator>
);

const MenuInner = styled.div`
  padding: 12px;
`;

const getSelectLimit = (selectProps: object): number | undefined => {
  if (!("limit" in selectProps) || typeof selectProps.limit !== "number") {
    return undefined;
  }

  return selectProps.limit;
};

const Menu = (props: MenuProps<LegacySelectOption, true, SelectGroup>) => {
  const { children, selectProps, getValue } = props;
  const limit = getSelectLimit(selectProps) ?? Number.POSITIVE_INFINITY;
  const optionSelectedLength = getValue().length || 0;
  return (
    <components.Menu {...props}>
      {optionSelectedLength < limit ? (
        children
      ) : (
        <MenuInner>
          <FormattedMessage id="containers.Select.maxLimit" defaultMessage="Max limit achieved" />
        </MenuInner>
      )}
    </components.Menu>
  );
};

const hasSelectError = (selectProps: object): boolean =>
  "error" in selectProps && Boolean(selectProps.error);

interface SelectColorState {
  isDisabled: boolean;
  isFocused: boolean;
  selectProps: object;
}

const getBorderColor = (state: SelectColorState): string => {
  if (hasSelectError(state.selectProps)) return "#ac231d";
  if (state.isFocused) return "#c2e5e7";
  return "#ccc";
};

const getBackgroundColor = (state: SelectColorState): string => {
  if (hasSelectError(state.selectProps)) return "#f5d1d2";
  if (state.isDisabled) return "#EFEFEF26";
  return "#14171c";
};

export const customStyles = {
  input: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: "#cccccc"
  }),
  control: (provided: CSSObjectWithLabel, state: SelectColorState) => ({
    ...provided,
    background: getBackgroundColor(state),
    boxShadow:
      state.isFocused && !hasSelectError(state.selectProps) ? "0px 0px 5px #c2e5e7" : undefined,
    border: `1px solid ${getBorderColor(state)}`,
    "&:hover": {
      borderColor: getBorderColor(state)
    }
  }),
  multiValueRemove: (provided: CSSObjectWithLabel) => ({
    ...provided,
    color: "#14171c",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#ffcdc7",
      color: "#ac231d"
    }
  }),
  indicatorsContainer: (provided: CSSObjectWithLabel, state: { selectProps: object }) => {
    const display = !hasSelectError(state.selectProps) ? "flex" : "none";
    return { ...provided, display };
  },
  clearIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    "&:hover": {
      color: "#ac231d",
      cursor: "pointer"
    }
  }),
  menu: (provided: CSSObjectWithLabel) => ({
    ...provided,
    backgroundColor: "#14171c",
    zIndex: 10
  }),
  multiValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    background: "#dff7f6"
  }),
  dropdownIndicator: (provided: CSSObjectWithLabel) => ({
    ...provided,
    "&:hover": {
      color: theme.primaryColor,
      cursor: "pointer"
    }
  }),
  option: (provided: CSSObjectWithLabel) => ({
    ...provided,
    "&:hover": {
      cursor: "pointer"
    }
  })
};

type MultiSelectProps = Omit<LegacySelectProps, "theme"> & {
  selectTheme: LegacySelectProps["theme"];
};

const MultiSelect = ({ selectTheme, ...props }: MultiSelectProps) => (
  <Select<LegacySelectOption, true, SelectGroup> {...props} theme={selectTheme} />
);

const UpStyledReactSelect = styled(MultiSelect)<StyledSelectProps>`
  font-size: 12px;
  margin: ${(props) => props.margin || "unset"};
  ${(props) => (props.flex ? `flex: ${props.flex};` : "flex-grow: 1;")};
  height: 100% !important;
  width: ${(props) => props.width || "auto"};
  min-width: ${(props) => props.minWidth || 0};
`;

const UpReactSelect = (props: UpReactSelectProps) => (
  <UpStyledReactSelect
    {...props}
    styles={customStyles}
    components={props.limit ? { ClearIndicator, Menu } : { ClearIndicator }}
    selectTheme={(selectTheme: Theme) => getTheme(selectTheme)}
    isMulti
  />
);

export { UpReactSelect };

export const getTheme = (styleTheme: Theme): Theme => ({
  ...styleTheme,
  borderRadius: 3,
  colors: {
    ...styleTheme.colors,
    primary: "#c2e5e7",
    primary25: "#1f242e",
    primary50: "#c2e5e7"
  }
});

interface ImmutableSelectValue {
  toJS(): LegacySelectOption[];
}

interface ImmutableSelectInput {
  name: string;
  value?: ImmutableSelectValue;
  onChange(value: unknown): void;
}

type ImmutableMultiSelectProps = Omit<FieldHocProps, "input"> & {
  input: ImmutableSelectInput;
  customProps: {
    options: LegacySelectOption[];
    minWidth?: string;
  };
  placeholder?: string;
  limit?: number;
};

const unwrappedMultiSelectBox = (props: ImmutableMultiSelectProps) => {
  const {
    input: { value, name, onChange: handleInputChange },
    customProps: { options, minWidth },
    meta: { error, submitFailed },
    placeholder,
    limit
  } = props;

  const inputValue: LegacySelectOption[] = value
    ? limit
      ? value.toJS().slice(0, limit)
      : value.toJS()
    : [];

  return (
    <UpReactSelect
      options={options}
      value={inputValue}
      name={name}
      onChange={(selectedOptions: MultiValue<LegacySelectOption> | null) => {
        handleInputChange(fromJS(selectedOptions ?? []));
      }}
      minWidth={minWidth}
      inputDisabled
      placeholder={placeholder}
      error={error && submitFailed}
      limit={limit}
      width="100%"
    />
  );
};

const multiSelectBox = FieldsHOC(unwrappedMultiSelectBox);

export { multiSelectBox };

const getValues = (options: LegacySelectOption[], value: unknown[]): LegacySelectOption[] =>
  options && options.filter((v) => value.includes(v.value));

interface PlainSelectInput {
  name: string;
  value: true | unknown[];
  onChange(value: unknown[]): void;
}

type PlainMultiSelectProps = Omit<FieldHocProps, "input"> & {
  input: PlainSelectInput;
  options: LegacySelectOption[];
  placeholder?: string;
  minWidth?: string;
};

const unwrappedMultiSelectBoxNonImmutable = (props: PlainMultiSelectProps) => (
  <UpReactSelect
    options={props.options}
    value={props.input.value === true ? [] : getValues(props.options, props.input.value)}
    name={props.input.name}
    onChange={(selectedOptions: MultiValue<LegacySelectOption> | null) => {
      props.input.onChange((selectedOptions ?? []).map((option) => option.value));
    }}
    minWidth={props.minWidth}
    inputDisabled
    placeholder={props.placeholder}
    error={props.meta.error && props.meta.submitFailed}
    width="100%"
  />
);

const multiSelectBoxNonImmutable = FieldsHOC(unwrappedMultiSelectBoxNonImmutable);

export { multiSelectBoxNonImmutable };
